/**
 * Header style Amazon pour FrontOffice
 * Design inspiré Amazon avec barre de navigation foncée
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useCart, catalogApi, ArticleCategory, enterpriseApi, EnterprisePublicInfo, ArticleWithStock, getBrandInfo, hasStock } from '@e-store/shared';
import './AmazonHeader.css';

interface AmazonHeaderProps {
  showSearch?: boolean;
  onSearch?: (term: string) => void;
  initialSearchTerm?: string;
}

export function AmazonHeader({ showSearch = true, onSearch, initialSearchTerm = '' }: AmazonHeaderProps) {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { cart } = useCart();

  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [enterprise, setEnterprise] = useState<EnterprisePublicInfo | null>(null);
  const brand = getBrandInfo();

  // Search autocomplete
  const [suggestions, setSuggestions] = useState<ArticleWithStock[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const accountMenuRef = useRef<HTMLDivElement>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadCategories();
    loadEnterpriseInfo();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setShowAccountMenu(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search suggestions debounce
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (searchTerm.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    searchDebounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await catalogApi.getProducts({
          search: searchTerm,
          categoryId: selectedCategory || undefined,
          page: 1,
          pageSize: 4,
        });
        if (response.data?.products) {
          setSuggestions(response.data.products);
          setShowSuggestions(true);
        }
      } catch (err) {
        console.error('Erreur recherche:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchTerm, selectedCategory]);

  const loadEnterpriseInfo = async () => {
    try {
      const response = await enterpriseApi.getPublicInfo();
      if (response.data) {
        setEnterprise(response.data);
      }
    } catch (err) {
      console.error('Erreur chargement infos entreprise:', err);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await catalogApi.getCategories();
      if (response.data) {
        setCategories(response.data);
      }
    } catch (err) {
      console.error('Erreur chargement catégories:', err);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(searchTerm);
    } else {
      // Navigate to catalog with search
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      if (selectedCategory) params.set('category', selectedCategory);
      navigate(`/catalog?${params.toString()}`);
    }
  };

  const handleCategorySelect = (categoryId: string, categoryName: string) => {
    setSelectedCategory(categoryId);
    setShowCategoryDropdown(false);
    // Navigate to category
    navigate(`/catalog?category=${categoryId}`);
  };

  const handleSuggestionClick = (productId: string) => {
    setShowSuggestions(false);
    navigate(`/product/${productId}`);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchFocus = () => {
    if (suggestions.length > 0 && searchTerm.length >= 2) {
      setShowSuggestions(true);
    }
  };

  const handleLogout = async () => {
    setShowAccountMenu(false);
    await logout();
    navigate('/');
  };

  return (
    <header className="amazon-header">
      {/* Top Bar - Dark */}
      <div className="amazon-header-main">
        <div className="amazon-header-content">
          {/* Logo */}
          <Link to="/" className="amazon-logo">
            {enterprise?.logoUrl ? (
              <img src={enterprise.logoUrl} alt={enterprise.name || brand.name} className="amazon-logo-img" />
            ) : (
              <div className="amazon-logo-text">
                <span className="logo-name">{enterprise?.name || brand.name}</span>
                {brand.suffix && <span className="logo-suffix">{brand.suffix}</span>}
              </div>
            )}
          </Link>

          {/* Search Bar */}
          {showSearch && (
            <div className="amazon-search-wrapper" ref={searchWrapperRef}>
              <form className="amazon-search" onSubmit={handleSearch}>
                <div className="search-category-wrapper" ref={categoryDropdownRef}>
                  <button
                    type="button"
                    className="search-category-btn"
                    onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                  >
                    <span>{selectedCategory ? categories.find(c => c.id === selectedCategory)?.name || 'Toutes' : 'Toutes catégories'}</span>
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor">
                      <path d="M0 0L5 6L10 0H0Z"/>
                    </svg>
                  </button>

                  {showCategoryDropdown && (
                    <div className="search-category-dropdown">
                      <button
                        type="button"
                        className={`category-option ${!selectedCategory ? 'active' : ''}`}
                        onClick={() => { setSelectedCategory(''); setShowCategoryDropdown(false); }}
                      >
                        Toutes les catégories
                      </button>
                      {categories.map(cat => (
                        <button
                          key={cat.id}
                          type="button"
                          className={`category-option ${selectedCategory === cat.id ? 'active' : ''}`}
                          onClick={() => handleCategorySelect(cat.id, cat.name)}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <input
                  type="text"
                  className="search-input"
                  placeholder="Rechercher un produit..."
                  value={searchTerm}
                  onChange={handleSearchInputChange}
                  onFocus={handleSearchFocus}
                />

                <button type="submit" className="search-btn">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </button>
              </form>

              {/* Search Suggestions */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="search-suggestions">
                  {suggestions.map(product => {
                    const mainImage = product.images && product.images.length > 0
                      ? product.images[product.mainImageIndex || 0]
                      : null;
                    const priceTTC = product.priceTTC || (product.priceHT * (1 + product.tvaRate / 100));

                    return (
                      <button
                        key={product.id}
                        type="button"
                        className="suggestion-item"
                        onClick={() => handleSuggestionClick(product.id)}
                      >
                        <div className="suggestion-image">
                          {mainImage ? (
                            <img src={mainImage} alt={product.name} />
                          ) : (
                            <div className="no-image">📦</div>
                          )}
                        </div>
                        <div className="suggestion-info">
                          <span className="suggestion-name">{product.name}</span>
                          <span className="suggestion-ref">Réf: {product.reference}</span>
                          {hasStock(product.type) && (
                            <span className={`suggestion-stock ${product.isInStock ? 'in-stock' : 'out-stock'}`}>
                              {product.isInStock ? '✓ En stock' : '✗ Indisponible'}
                            </span>
                          )}
                        </div>
                        <div className="suggestion-price-wrapper">
                          <span className="suggestion-price">{priceTTC.toLocaleString('fr-MG')} Ar</span>
                        </div>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    className="suggestion-all"
                    onClick={() => {
                      setShowSuggestions(false);
                      handleSearch({ preventDefault: () => {} } as React.FormEvent);
                    }}
                  >
                    Voir tous les résultats pour "{searchTerm}"
                  </button>
                </div>
              )}
              {showSuggestions && searchLoading && (
                <div className="search-suggestions">
                  <div className="suggestion-loading">Recherche en cours...</div>
                </div>
              )}
            </div>
          )}

          {/* Right Actions */}
          <div className="amazon-actions">
            {/* Account */}
            <div className="amazon-account" ref={accountMenuRef}>
              <button
                className="amazon-account-btn"
                onClick={() => setShowAccountMenu(!showAccountMenu)}
              >
                <span className="account-greeting">
                  {isAuthenticated ? `Bonjour, ${user?.firstName}` : 'Bonjour, Identifiez-vous'}
                </span>
                <span className="account-label">
                  Compte et listes
                  <svg width="8" height="5" viewBox="0 0 8 5" fill="currentColor">
                    <path d="M0 0L4 5L8 0H0Z"/>
                  </svg>
                </span>
              </button>

              {showAccountMenu && (
                <div className="amazon-account-dropdown">
                  {isAuthenticated ? (
                    <>
                      <div className="dropdown-header">
                        <span className="user-name">{user?.firstName} {user?.lastName}</span>
                        <span className="user-email">{user?.email}</span>
                      </div>
                      <div className="dropdown-divider"></div>
                      <Link to="/my-account" className="dropdown-link" onClick={() => setShowAccountMenu(false)}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                          <circle cx="12" cy="7" r="4"/>
                        </svg>
                        Mon Compte
                      </Link>
                      <Link to="/orders" className="dropdown-link" onClick={() => setShowAccountMenu(false)}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                          <line x1="3" y1="6" x2="21" y2="6"/>
                          <path d="M16 10a4 4 0 0 1-8 0"/>
                        </svg>
                        Mes Commandes
                      </Link>
                      <div className="dropdown-divider"></div>
                      <button className="dropdown-link logout" onClick={handleLogout}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                          <polyline points="16 17 21 12 16 7"/>
                          <line x1="21" y1="12" x2="9" y2="12"/>
                        </svg>
                        Déconnexion
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" className="dropdown-signin-btn" onClick={() => setShowAccountMenu(false)}>
                        Se connecter
                      </Link>
                      <p className="dropdown-signup-text">
                        Nouveau client ? <Link to="/register" onClick={() => setShowAccountMenu(false)}>Créer un compte</Link>
                      </p>
                      <div className="dropdown-divider"></div>
                      <div className="dropdown-links-grid">
                        <div>
                          <h4>Vos listes</h4>
                          <Link to="/login" onClick={() => setShowAccountMenu(false)}>Créer une liste</Link>
                        </div>
                        <div>
                          <h4>Votre compte</h4>
                          <Link to="/login" onClick={() => setShowAccountMenu(false)}>Compte</Link>
                          <Link to="/login" onClick={() => setShowAccountMenu(false)}>Commandes</Link>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Orders */}
            <Link to={isAuthenticated ? "/orders" : "/login"} className="amazon-orders">
              <span className="orders-label-top">Retours</span>
              <span className="orders-label-main">et Commandes</span>
            </Link>

            {/* Cart */}
            <Link to="/cart" className="amazon-cart">
              <div className="cart-icon-wrapper">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="9" cy="21" r="1"/>
                  <circle cx="20" cy="21" r="1"/>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                </svg>
                <span className="cart-count">{cart?.itemCount || 0}</span>
              </div>
              <span className="cart-label">Panier</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom Bar - Navigation */}
      <nav className="amazon-nav">
        <div className="amazon-nav-content">
          <Link to="/catalog" className="nav-item">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
            Toutes les catégories
          </Link>
          {categories.slice(0, 8).map(cat => (
            <Link key={cat.id} to={`/catalog?category=${cat.id}`} className="nav-item">
              {cat.name}
            </Link>
          ))}
          <Link to="/faq" className="nav-item">
            ❓ FAQ
          </Link>
        </div>
      </nav>
    </header>
  );
}
